import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import './Account.css';


const Account = () => {
  const navigate = useNavigate();
  const { cart, getCartItemsCount, getCartTotal, clearCart } = useCart();
  const { wishlist, getWishlistItemsCount, clearWishlist, loadUserWishlist } = useWishlist();
  const [user, setUser] = useState(null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);


  useEffect(() => {
    // Check if user is logged in
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        setUser(userData);
        
        // Load user's saved wishlist when they log in
        if (userData.id) {
          console.log('Loading user wishlist for user:', userData.id);
          loadUserWishlist(userData.id);
        }
      } catch (e) {
        console.error('Error parsing saved user:', e);
        navigate('/');
      }
    } else {
      // Redirect to home if not logged in
      navigate('/');
    }
  }, [navigate, loadUserWishlist]);

  const handleLogout = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    setUser(null);
    localStorage.removeItem('user');
    navigate('/');
  };

  const cancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  const viewOrders = () => {
    // Check if there are any saved orders
    const orders = JSON.parse(localStorage.getItem('paypal_orders') || '[]');
    if (orders.length > 0) {
      alert(`You have ${orders.length} saved orders. Check your downloads folder for order receipts.`);
    } else {
      alert('No orders found.');
    }
  };

  const clearCartData = () => {
    if (window.confirm('Are you sure you want to clear your cart? This action cannot be undone.')) {
      clearCart();
      alert('Cart cleared successfully!');
    }
  };

  const clearWishlistData = () => {
    if (window.confirm('Are you sure you want to clear your wishlist? This action cannot be undone.')) {
      clearWishlist();
      alert('Wishlist cleared successfully!');
    }
  };

  if (!user) {
    return (
      <div className="account-page">
        <div className="container">
          <div className="loading">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="account-page">
      <div className="container">
        <div className="account-header">
          <h1>My Account</h1>
          <p>Welcome back, {user.name?.split(' ')[0] || user.email?.split('@')[0]}!</p>
        </div>

        <div className="account-content">
          <div className="account-section">
            <h2>Profile Information</h2>
            <div className="profile-info">
              <div className="profile-item">
                <label>Name:</label>
                <span>{user.name}</span>
              </div>
              <div className="profile-item">
                <label>Email:</label>
                <span>{user.email}</span>
              </div>
              {user.picture && (
                <div className="profile-item">
                  <label>Profile Picture:</label>
                  <img src={user.picture} alt="Profile" className="profile-picture" />
                </div>
              )}
              <div className="profile-item">
                <label>Member Since:</label>
                <span>{new Date().toLocaleDateString()}</span>
              </div>
            </div>
          </div>

          <div className="account-section">
            <h2>Shopping Summary</h2>
            <div className="shopping-summary">
              <div className="summary-card">
                <div className="summary-icon">🛒</div>
                <div className="summary-info">
                  <h3>Cart Items</h3>
                  <p className="summary-count">{getCartItemsCount()} items</p>
                  <p className="summary-value">R {getCartTotal().toLocaleString()}</p>
                </div>
                <div className="summary-actions">
                  <button onClick={() => navigate('/cart')} className="btn btn-primary">View Cart</button>
                  <button onClick={clearCartData} className="btn btn-secondary">Clear Cart</button>
                </div>
              </div>

              <div className="summary-card">
                <div className="summary-icon">❤️</div>
                <div className="summary-info">
                  <h3>Wishlist Items</h3>
                  <p className="summary-count">{getWishlistItemsCount()} items</p>
                  <p className="summary-value">Saved for later</p>
                </div>
                <div className="summary-actions">
                  <button onClick={() => navigate('/wishlist')} className="btn btn-primary">View Wishlist</button>
                  <button onClick={clearWishlistData} className="btn btn-secondary">Clear Wishlist</button>
                </div>
              </div>

              <div className="summary-card">
                <div className="summary-icon">📋</div>
                <div className="summary-info">
                  <h3>Order History</h3>
                  <p className="summary-count">View receipts</p>
                  <p className="summary-value">Download available</p>
                </div>
                <div className="summary-actions">
                  <button onClick={viewOrders} className="btn btn-primary">View Orders</button>
                </div>
              </div>
            </div>
          </div>

          <div className="account-section">
            <h2>Account Actions</h2>
            <div className="account-actions">
              <button onClick={() => navigate('/')} className="btn btn-secondary">
                Continue Shopping
              </button>
              <button onClick={() => navigate('/specialist')} className="btn btn-secondary">
                Book Consultation
              </button>
              <button onClick={handleLogout} className="btn btn-danger">
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="logout-confirm-modal">
          <div className="logout-modal-content">
            <div className="logout-modal-header">
              <h3>Confirm Logout</h3>
              <button className="logout-modal-close" onClick={cancelLogout}>
                ×
              </button>
            </div>
            <div className="logout-modal-body">
              <p>Are you sure you want to log out?</p>
              <p className="logout-note">Your cart and wishlist data will be saved and restored when you log back in.</p>
              <div className="logout-modal-actions">
                <button 
                  className="logout-modal-cancel"
                  onClick={cancelLogout}
                >
                  Cancel
                </button>
                <button 
                  className="logout-modal-confirm"
                  onClick={confirmLogout}
                >
                  Yes, Logout
                </button>
              </div>
            </div>
          </div>
          <div className="logout-modal-overlay" onClick={cancelLogout}></div>
        </div>
      )}
    </div>
  );
};

export default Account;
